import * as FileSaver from 'file-saver';
import {
  calculateEquivalentShares,
} from '../calculators/contribution';
import {
  convertDate,
  convertUTCDateInTimeZone,
  formatDate,
  inclusiveDayDiff,
} from '../calculators/date';
import {
  convertToDollar,
} from '../calculators/money';
import {
  ContributionStatus,
} from '../constants/ContributionStatus';
import {
  DateFormats,
} from '../constants/DateFormats';
import {
  PeriodDisplayTypes,
} from '../constants/PeriodDisplayType';
import {
  groupBy,
  getDetailsFromKey,
} from '../utils';
const fileType =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
const fileExtension = '.xlsx';


/**
 *
 * @param {string} companyTimezone - US/Mountain
 * @param {periods} periods - ann array of periods to export
 * @param {long} selectedPeriod - the period selected, typically the first in the list of periods
 * @param {contributions} contributions -- array of contributions
 * @param {String} types -- always ["showAll"]
 * @param {number} totalTranche number of traunches to display
 * Number(planDetail[0].lookBack) / Number(planDetail[0].purchasePeriod);
 * @return {?} a list of rows for the table
 */
export function formatExpenseTableData(companyTimezone, periods, selectedPeriod, contributions, types, totalTranche) {
  const tableRows = [];

  let sPeriod = selectedPeriod ? selectedPeriod.period : null;
  if (
    selectedPeriod &&
    selectedPeriod.type === PeriodDisplayTypes.CONTRIBUTION
  ) {
    sPeriod = null;
  }

  let displayPeriods = periods;
  if (sPeriod) {
    displayPeriods = [sPeriod];
  }
  const typeContributions = groupContributionByUserAndType(
      contributions,
      displayPeriods,
  );

  if (types.includes('showAll') || types.includes('current')) {
    if (ContributionStatus.NEW in typeContributions) {
      const generatedRows = generateRowJson(
          typeContributions[ContributionStatus.NEW],
          ContributionStatus.NEW,
          totalTranche, {
            ...sPeriod,
          },
          companyTimezone,
      );
      if (generatedRows) {
        tableRows.push(...generatedRows);
      }
    }

    if (ContributionStatus.INCREASE in typeContributions) {
      const generatedRows = generateRowJson(
          typeContributions[ContributionStatus.INCREASE],
          ContributionStatus.INCREASE,
          totalTranche, {
            ...sPeriod,
          },
          companyTimezone,
      );
      if (generatedRows) {
        tableRows.push(...generatedRows);
      }
    }
  }
  if (types.includes('showAll') || types.includes('withdrawals')) {
    if (ContributionStatus.WITHDRAW in typeContributions) {
      const generatedRows = generateRowJson(
          typeContributions[ContributionStatus.WITHDRAW],
          ContributionStatus.WITHDRAW,
          totalTranche, {
            ...sPeriod,
          },
          companyTimezone,
      );
      if (generatedRows) {
        tableRows.push(...generatedRows);
      }
    }
  }
  if (types.includes('showAll') || types.includes('terminations')) {
    if (ContributionStatus.TERMINATE in typeContributions) {
      const generatedRows = generateRowJson(
          typeContributions[ContributionStatus.TERMINATE],
          ContributionStatus.TERMINATE,
          totalTranche, {
            ...sPeriod,
          },
          companyTimezone,
      );
      if (generatedRows) {
        tableRows.push(...generatedRows);
      }
    }
  }
  if (types.includes('showAll')) {
    if (ContributionStatus.RESET in typeContributions) {
      const generatedRows = generateRowJson(
          typeContributions[ContributionStatus.RESET],
          ContributionStatus.RESET,
          totalTranche, {
            ...sPeriod,
          },
          companyTimezone,
      );
      if (generatedRows) {
        tableRows.push(...generatedRows);
      }
    }
  }
  if (types.includes('showAll')) {
    if (ContributionStatus.RESET_NEW in typeContributions) {
      const generatedRows = generateRowJson(
          typeContributions[ContributionStatus.RESET_NEW],
          ContributionStatus.RESET_NEW,
          totalTranche, {
            ...sPeriod,
          },
          companyTimezone,
      );
      if (generatedRows) {
        tableRows.push(...generatedRows);
      }
    }
  }
  return tableRows;
}

/**
 *
 * @param {*} currentContributions
 * @param {*} periods
 * @return {typeContributions}
 */
export function groupContributionByUserAndType(currentContributions, periods) {
  console.log('groupContributionByUserAndType', periods);
  // Let's first group contribution by user and sort by contribution date
  const contributionsByUser = groupContributionByUser(currentContributions, periods);
  // Sort them by created at
  // eslint-disable-next-line guard-for-in
  for (const key in contributionsByUser) {
    contributionsByUser[key] = contributionsByUser[key].sort(
        (a, b) => b.createdAt - a.createdAt,
    );
  }
  const typeContributions = {};
  // Sort them by created at
  // eslint-disable-next-line guard-for-in
  for (const key in contributionsByUser) {
    const userContributions = contributionsByUser[key];

    // We only need to get the last contribution type of each user
    const lastContributionType = userContributions[0].type;
    // Group contribution by created at in 2-d array [[date1],[date2]]
    const userContributionsByType = [];
    let arrayOfContribution = [];
    let lastmodificationType = lastContributionType;
    for (const uc of userContributions) {
      if (lastmodificationType !== uc.type) {
        userContributionsByType.push(arrayOfContribution);
        lastmodificationType = uc.type;
        arrayOfContribution = [];
      }
      arrayOfContribution.push(uc);
    }
    if (arrayOfContribution && arrayOfContribution.length > 0) {
      userContributionsByType.push(arrayOfContribution);
    }
    // If last state of contribution is withdraw or terminate then we need to create
    // a row from each previous state to this state
    if (
      lastContributionType === ContributionStatus.WITHDRAW ||
      lastContributionType === ContributionStatus.TERMINATE
    ) {
      // We want to create row from each type to last contribution type
      for (const array of userContributionsByType) {
        if (array[0].type !== lastContributionType) {
          // Merge all filtered contributions
          if (!(lastContributionType in typeContributions)) {
            typeContributions[lastContributionType] = [];
          }
          typeContributions[lastContributionType].push([
            ...userContributionsByType[0],
            ...array,
          ]);
        }
      }
    } else {
      for (const array of userContributionsByType) {
        // Merge all filtered contributions
        if (!(array[0].type in typeContributions)) {
          typeContributions[array[0].type] = [];
        }
        if (array[0].type === ContributionStatus.INCREASE) {
          const groupedByTranche = groupBy(array, 'modificationDate');
          const allTranches = Object.keys(groupedByTranche);
          for (const tranche of allTranches) {
            const trancheWiseRows = groupedByTranche[tranche];
            typeContributions[array[0].type].push(trancheWiseRows);
          }
        } else {
          typeContributions[array[0].type].push(array);
        }
      }
    }
  }
  return typeContributions;
}

/**
 *
 * @param {*} contributions
 * @param {*} periods
 * @return {contributions}
 */
export function groupContributionByUser(contributions, periods) {
  const sortedPeriods = periods.sort(
      (a, b) => b.periodStartdate - a.periodStartdate,
  );
  // Let's first group contribution by user
  const contributionsByUser = {};
  for (const current of contributions) {
    const user = getDetailsFromKey(current.object_id);
    for (const period of sortedPeriods) {
      let contributionDetails = current[`pp_${period.periodStartdate}`];
      if (
        !contributionDetails ||
        contributionDetails.length === 0 ||
        contributionDetails.type === ContributionStatus.DECREASE
      ) {
        continue;
      }
      // denormalize some contribution properties into contributionDetails objects to match legacy data structure
      contributionDetails = contributionDetails.map((c) => {
        // c.purchasePrice = current.purchasePrice;
        c.empid = current.empid;
        c.email = current.email;
        c.objectid = current.object_id;
        c.firstName = current.firstName;
        c.lastName = current.lastName;
        c.purchaseExpiration = current.purchaseExpiration;
        c.offerStartdate = current.offerStartdate;
        c.contributedByTaabl = current.GSI1_sort_key.includes('true');
        c.department = current.department;
        c.subsidiary = current.subsidiary;
        if (c.type !== ContributionStatus.RESET) {
          c.purchasePrice = current.purchasePrice;
        }
        return c;
      });
      if (!contributionsByUser[user]) {
        contributionsByUser[user] = [];
      }
      contributionDetails.map((c)=> {
        contributionsByUser[user].push({
          ...c,
        });
      });
      // contributionsByUser[user] = contributionsByUser[user].concat(
      //     contributionDetails,
      // );
    }
  }
  return contributionsByUser;
}

/**
 *
 * @param {*} rows
 * @param {*} type
 * @param {*} totalTranche
 * @param {*} selectedPeriod
 * @param {*} companyTimezone
 * @return {?}
 */
export function generateRowJson(rows, type = null, totalTranche, selectedPeriod, companyTimezone) {
  const contributionElection = [];
  const grantFairValue = [];

  const calculatedRows = [];
  for (const row of rows) {
    for (let i = 0; i < totalTranche; i++) {
      contributionElection[i] = 0;
      grantFairValue[i] = 0;
    }
    // Sort user contributions by date
    const allUserContributions = [...row];
    const lastContribution = allUserContributions[0];
    const allNewContributions = allUserContributions
        .filter(
            (c) =>
              c.type === ContributionStatus.NEW ||
        c.type === ContributionStatus.INCREASE ||
        c.type === ContributionStatus.RESET ||
        c.type === ContributionStatus.RESET_NEW,
        )
        .sort((a, b) => a.createdAt - b.createdAt);

    // If no new contributions then continue. This may be case when we are doing decrease + withdraw/terminate
    if (!allNewContributions || allNewContributions.length === 0) {
      continue;
    }
    for (const contribution of allNewContributions) {
      if (
        'blackSchole' in contribution &&
        contribution.blackSchole &&
        Object.keys(contribution.blackSchole).length > 0
      ) {
        for (let tranche = 0; tranche < totalTranche; tranche++) {
          contributionElection[tranche] = contribution.contributionValue;
          if (contribution.tranche === (tranche + 1) && contribution.blackSchole) {
            grantFairValue[tranche] = contribution.blackSchole.grantDateFairValue;
          }
        }
      }
    }

    let purchasePrice = allNewContributions[0].purchasePrice;
    const stockPrice = allNewContributions[0].stockPrice;
    let empName = '';
    if (
      'firstName' in lastContribution &&
      lastContribution.firstName &&
      'lastName' in lastContribution &&
      lastContribution.lastName
    ) {
      empName = lastContribution.firstName + ' ' + lastContribution.lastName;
    }
    if (type === ContributionStatus.RESET && lastContribution.purchasePrice) {
      purchasePrice = lastContribution.purchasePrice;
    }
    const newRow = {
      type: type.charAt(0),
      empid: lastContribution.empid || lastContribution.email || '',
      userId: lastContribution.empid + '|' + lastContribution.objectid,
      employeeName: empName,
      costCenter: lastContribution.department,
      subsidiary: lastContribution.subsidiary || '',
      period: 1,
      start: lastContribution.offerStartdate,
      end: lastContribution.purchaseExpiration,
      stockPrice: convertToDollar(stockPrice),
      orgPrice: convertToDollar(purchasePrice),
      contributedByTaabl: lastContribution.contributedByTaabl,
    };
    if (type === ContributionStatus.RESET) {
      newRow.start = lastContribution.modificationDate;
    }
    if (type === ContributionStatus.RESET_NEW) {
      newRow.type = 'rn';
    }

    let sDate = convertUTCDateInTimeZone(newRow.start, companyTimezone);
    // If it is an increase row we need offering period start as modification date
    if (type === ContributionStatus.INCREASE) {
      sDate = convertUTCDateInTimeZone(lastContribution.modificationDate, companyTimezone);
    }
    if (type === ContributionStatus.WITHDRAW ||
      type === ContributionStatus.TERMINATE) {
      // check for any previous modifications
      const modifications = row.filter((item) => item.type === ContributionStatus.INCREASE ||
        item.type === ContributionStatus.DECREASE);

      if (modifications.length > 0) {
        sDate = convertUTCDateInTimeZone(modifications[0].modificationDate, companyTimezone);
      }
    }
    let sum = 0;
    let value = 0;
    for (let i = 0; i < totalTranche; i++) {
      newRow[`election${i + 1}`] = grantFairValue[i] !== 0 ?
        convertToDollar(contributionElection[i]) :
        0;
      newRow[`shares${i + 1}`] = grantFairValue[i] !== 0 ?
        calculateEquivalentShares(contributionElection[i], purchasePrice) :
        0;
      newRow[`value${i + 1}`] = grantFairValue[i] ? grantFairValue[i] : 0;
      value = grantFairValue[i] ? grantFairValue[i] : 0;
      sum += newRow[`shares${i + 1}`] * value;
      // This is done for purchase period. Since in purchase period we always show constant column
      if (allNewContributions[0].tranche === (i + 1)) {
        newRow.election = newRow[`election${i + 1}`];
        newRow.shares = newRow[`shares${i + 1}`];
        newRow.value = newRow[`value${i + 1}`];
      }
    }
    newRow.totalValue = sum;
    newRow.periodDays = inclusiveDayDiff(sDate, convertUTCDateInTimeZone(newRow.end, companyTimezone));
    newRow.startDate = sDate;
    newRow.costPerDay = allNewContributions[0].expensePerDay;
    // calculate monthly expenses
    let monthsArray = [];
    if (!selectedPeriod || Object.keys(selectedPeriod).length === 0) {
      // This code is commented because of issue 1197
      // if (type === ContributionStatus.RESET) {
      //     monthsArray = getMonthIntervals(
      //         lastContribution.periodStartdate,
      //         lastContribution.purchaseExpiration,
      //         companyTimezone
      //     );
      // } else
      monthsArray = getMonthIntervals(
          lastContribution.offerStartdate,
          lastContribution.purchaseExpiration,
          companyTimezone,
      );
    } else {
      monthsArray = getMonthIntervals(
          selectedPeriod.periodStartdate,
          selectedPeriod.periodEnddate,
          companyTimezone,
      );
    }
    let startedMonth = null;
    if ('modificationDate' in allNewContributions[0] && allNewContributions[0].modificationDate) {
      startedMonth = convertUTCDateInTimeZone(allNewContributions[0].modificationDate, companyTimezone);
    }
    if (
      type &&
      !allNewContributions[0].modificationDate &&
      (type === ContributionStatus.WITHDRAW ||
        type === ContributionStatus.TERMINATE)
    ) {
      startedMonth = convertUTCDateInTimeZone(allNewContributions[0].offerStartdate, companyTimezone);
    }

    let i = 0;
    const expenses = [];
    while (monthsArray[i] && 'lastDayofMonth' in monthsArray[i] &&
      convertUTCDateInTimeZone(monthsArray[i].lastDayofMonth, companyTimezone).isBefore(startedMonth)) {
      expenses.push({
        key: formatDate(
            convertUTCDateInTimeZone(monthsArray[i].lastDayofMonth, companyTimezone),
            DateFormats.MMDDYYYY,
        ),
        value: 0,
      });
      i++;
    }
    let modifiedMonth = null;
    if (
      'modificationDate' in allUserContributions[0] &&
      allUserContributions[0].modificationDate
    ) {
      modifiedMonth = convertUTCDateInTimeZone(allUserContributions[0].modificationDate, companyTimezone);
    } else modifiedMonth = convertUTCDateInTimeZone(allUserContributions[0].offerStartdate, companyTimezone);

    for (let j = i; j < monthsArray.length; j++) {
      const lastDayofMonth = convertUTCDateInTimeZone(monthsArray[j].lastDayofMonth, companyTimezone);
      // Check if withdraw and terminate and it happened in this month
      if (
        type &&
        (type === ContributionStatus.WITHDRAW ||
          type === ContributionStatus.TERMINATE) &&
        lastDayofMonth.isSameOrAfter(modifiedMonth)
      ) {
        if (modifiedMonth.format('MMYYYY') === lastDayofMonth.format('MMYYYY')) {
          let expense = 0;
          if (type === ContributionStatus.TERMINATE) {
            const modifiedMonthStart = modifiedMonth.clone().startOf('month');
            let days = modifiedMonthStart.diff(
                convertUTCDateInTimeZone(lastContribution.offerStartdate, companyTimezone), 'days');
            if (
              'modificationDate' in allNewContributions[0] &&
              allNewContributions[0].modificationDate) {
              days = modifiedMonthStart.diff(
                  convertUTCDateInTimeZone(allNewContributions[0].modificationDate, companyTimezone), 'days');
            }
            expense = -1 * days * newRow.costPerDay;
          } else {
            const days = inclusiveDayDiff(modifiedMonth.clone().startOf('month'),
                convertUTCDateInTimeZone(lastContribution.purchaseExpiration, companyTimezone));
            expense = days * newRow.costPerDay;
          }
          expenses.push({
            key: convertDate(
                monthsArray[i].lastDayofMonth,
                DateFormats.MMDDYYYY,
                false,
                companyTimezone,
            ),
            value: convertToDollar(expense, false),
          });
        }
        break;
      }

      let days = monthsArray[i].days;
      // Check if modified happened in that month
      if (type && (type !== ContributionStatus.NEW && type !== ContributionStatus.RESET_NEW) &&
        modifiedMonth.format('MMYYYY') === lastDayofMonth.format('MMYYYY')) {
        days = inclusiveDayDiff(modifiedMonth, lastDayofMonth);
      }
      if (type && (type !== ContributionStatus.NEW && type !== ContributionStatus.RESET_NEW) &&
      startedMonth.format('MMYYYY') === lastDayofMonth.format('MMYYYY')) {
        days = inclusiveDayDiff(startedMonth, lastDayofMonth);
      }
      expenses.push({
        key: convertDate(
            monthsArray[i].lastDayofMonth,
            DateFormats.MMDDYYYY,
            false,
            companyTimezone,
        ),
        value: convertToDollar(days * newRow.costPerDay, false),
      });
      i++;
    }
    newRow.costPerDayInCent = newRow.costPerDay;
    newRow.costPerDay = convertToDollar(newRow.costPerDay);
    newRow.expenses = expenses;
    calculatedRows.push(newRow);
  }
  return calculatedRows;
}

/**
 *
 * @param {*} startDate
 * @param {*} endDate
 * @param {*} timezone
 * @return {?}
 **/
export function getMonthIntervals(startDate, endDate, timezone) {
  // Let's start from start of month
  const loopStartDate = convertUTCDateInTimeZone(startDate, timezone).startOf('month');
  const sDate = convertUTCDateInTimeZone(startDate, timezone);
  const eDate = convertUTCDateInTimeZone(endDate, timezone);
  const timeValues = [];
  while (loopStartDate.isBefore(eDate)) {
    let days = loopStartDate.daysInMonth();
    // Check if loop start date and start date are in same month example a period can start from Nov-5
    if (loopStartDate.format('MMYY') === sDate.format('MMYY')) {
      // We need to take days between sDate and eDate
      days = inclusiveDayDiff(sDate, sDate.clone().endOf('month'));
    }
    // Check if loop start date and end date are in same month example a period can start from Nov-5
    if (loopStartDate.format('MMYY') === eDate.format('MMYY')) {
      // We need to take days between sDate and eDate
      days = inclusiveDayDiff(loopStartDate, eDate);
    }

    timeValues.push({
      lastDayofMonth: loopStartDate.clone().endOf('month').valueOf(),
      days,
    });
    loopStartDate.add(1, 'month');
  }
  return timeValues;
}

/**
 * @param {*} tableData
 * @return {?}
 */
export function convertToTableData(tableData) {
  if (tableData && tableData.length > 0) {
    const rows = [];
    let index = 0;
    for (const r of tableData) {
      let convertedRow = {
        ...r,
      };
      const expenses = convertedRow.expenses.reduce((acc, current) => {
        acc[current.key] = current.value;
        return acc;
      }, {});
      convertedRow = Object.assign(convertedRow, expenses);
      delete convertedRow.expenses;
      convertedRow.id = index++;
      rows.push(convertedRow);
    }
    return rows;
  }
  return [];
}

/**
 * @deprecated -- exports are gong to need to be on the serverside for future use.
 * @param {*} sheetRows
 * @param {*} type
 */
export function writeToFile(sheetRows, type) {
  const ws = XLSX.utils.json_to_sheet(sheetRows, {
    header: ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K'],
    skipHeader: true,
  });

  const wb = {
    Sheets: {
      data: ws,
    },
    SheetNames: ['data'],
  };
  const excelBuffer = XLSX.write(wb, {
    bookType: 'xlsx',
    type: 'array',
  });

  const data = new Blob([excelBuffer], {
    type: fileType,
  });
  FileSaver.saveAs(data, `${type}Report${fileExtension}`);
}

/**
 *
 * @param {?} tableRows
 * @param {?} trancheList
 * @param {?} totalUsers
 * @param {?} selectedPeriod
 * @param {?} company
 * @return {*}
 */
export function generateTotalRow(tableRows, trancheList, totalUsers, selectedPeriod, company) {
  const newRow = {};
  let fairValue = 0;
  let expense = 0;
  let totalCostPerDay = 0;
  let totalCE = 0;
  let totalShares = 0;
  for (const row of tableRows) {
    for (const month of row.expenses) {
      expense += month.value;
    }
    for (const tranche of trancheList) {
      totalCE += row[`election${tranche + 1}`];
      totalShares += row[`shares${tranche + 1}`];
    }
    fairValue += row.totalValue;
    totalCostPerDay += row.costPerDayInCent;
  }
  newRow.fairValue = fairValue;
  newRow.expense = expense;
  newRow.avgCostPerDay = totalCostPerDay / totalUsers;

  newRow.avgContributionElection = totalCE / totalUsers;
  newRow.totalShares = Math.floor(totalShares / totalUsers);
  if (selectedPeriod && company) {
    const startDate = convertUTCDateInTimeZone(selectedPeriod.periodStartdate, company.timezone);
    const endDate = convertUTCDateInTimeZone(selectedPeriod.periodEnddate, company.timezone);
    const days = inclusiveDayDiff(startDate, endDate);
    const months = Math.floor(days / 30);
    newRow.avgCostPerMonth = (newRow.avgCostPerDay * days) / months;
  }
  return newRow;
}
